import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { SessionsList } from "./SessionsList";
import "./Session.css";
import { initializeSocket } from "../socket";

export const Sessions = ({
  selectedgrp,
  selectedDate,
  coachHomeGroups,
  aggregateCoachSessionsAcrossGroupsByDate = false,
  coachGroupsListReady = true,
  addSessionGroupOptions,
  onSessionAddGroupChosen,
  playerHomeMode = false,
}) => {
  const { t } = useTranslation();
  const [selectedSession, setSelectedSession] = useState(
    JSON.parse(localStorage.getItem("rsessn"))
  );
  const token = localStorage.getItem("token");
  const socket = initializeSocket(token);

  const coachHomeGroupIds = useMemo(() => {
    const rows = Array.isArray(coachHomeGroups) ? coachHomeGroups : [];
    return rows
      .map((g) => {
        const d = g?.groupDetails;
        return d?.gr_id || d?.dataValues?.gr_id || g?.gr_id;
      })
      .filter(Boolean);
  }, [coachHomeGroups]);

  useEffect(() => {
    if (playerHomeMode && coachHomeGroupIds.length) {
      const storedSession = JSON.parse(localStorage.getItem("rsessn"));
      const storedGrId = storedSession?.gr_id || storedSession?._groupId;
      if (
        storedSession?.ts_id &&
        storedGrId &&
        coachHomeGroupIds.includes(storedGrId)
      ) {
        setSelectedSession(storedSession);
      }
      return;
    }
    if (selectedgrp) {
      const storedSession = JSON.parse(localStorage.getItem("rsessn"));
      const storedGrId = storedSession?.gr_id || storedSession?._groupId;
      const belongsToSelected =
        storedSession &&
        storedSession?.ts_id &&
        storedGrId === selectedgrp.groupDetails.gr_id;
      const belongsToCoachHome =
        storedSession &&
        storedSession?.ts_id &&
        coachHomeGroupIds.length > 0 &&
        storedGrId &&
        coachHomeGroupIds.includes(storedGrId);

      if (belongsToSelected || belongsToCoachHome) {
        setSelectedSession(storedSession);
      } else {
        setSelectedSession(null);
        if (storedSession && storedGrId !== selectedgrp.groupDetails.gr_id) {
          if (!coachHomeGroupIds.length || !coachHomeGroupIds.includes(storedGrId)) {
            localStorage.removeItem("rsessn");
          }
        }
      }
    }
  }, [selectedgrp, coachHomeGroupIds, playerHomeMode]);

  // Listen for session selection events and localStorage changes
  useEffect(() => {
    const handleSessionSelected = () => {
      try {
        const storedSessionStr = localStorage.getItem("rsessn");
        if (!storedSessionStr) {
          // Only update if current state is not null
          if (selectedSession !== null) {
            setSelectedSession(null);
          }
          return;
        }

        const storedSession = JSON.parse(storedSessionStr);
        if (storedSession && storedSession.ts_id) {
          const sessionGrId = storedSession.gr_id || storedSession._groupId;
          const inCoachHome =
            coachHomeGroupIds.length > 0 &&
            sessionGrId &&
            coachHomeGroupIds.includes(sessionGrId);
          const inSelected =
            selectedgrp && sessionGrId === selectedgrp.groupDetails.gr_id;
          if (
            (playerHomeMode && inCoachHome) ||
            (selectedgrp && (inSelected || inCoachHome))
          ) {
            // Only update if the session ID is different to avoid infinite loops
            if (!selectedSession || selectedSession.ts_id !== storedSession.ts_id) {
              console.log("🔄 Sessions - Updating selectedSession from localStorage:", storedSession.ts_id);
              setSelectedSession(storedSession);
            }
          }
        }
      } catch (error) {
        console.error("Error parsing session from localStorage:", error);
      }
    };
    
    // Listen for custom events
    window.addEventListener("sessionSelected", handleSessionSelected);
    window.addEventListener("storage", handleSessionSelected);

    if (selectedgrp?.groupDetails?.gr_id || coachHomeGroupIds.length) {
      handleSessionSelected();
    }

    return () => {
      window.removeEventListener("sessionSelected", handleSessionSelected);
      window.removeEventListener("storage", handleSessionSelected);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedgrp?.groupDetails?.gr_id,
    selectedSession?.ts_id,
    coachHomeGroupIds.join(","),
    playerHomeMode,
  ]);
  
  // WebSocket listener to update selectedSession state in real-time
  useEffect(() => {
    if (!selectedSession?.ts_id) {
      return;
    }
    
    // Listen for exercise state updates that may affect session state
    const handleExerciseStateUpdate = (data) => {
      // When an exercise state changes, we need to re-fetch session data or update locally
      // For now, we'll update the session state based on exercise states
      // The backend should emit session state updates, but we'll also update locally
      console.log("🔔 Sessions - exerciseStateUpdate received, may affect session state:", data);
    };
    
    socket.on("exerciseStateUpdate", handleExerciseStateUpdate);
    
    return () => {
      socket.off("exerciseStateUpdate", handleExerciseStateUpdate);
    };
  }, [selectedSession?.ts_id, socket]);

  const showAllGroupsHeader =
    aggregateCoachSessionsAcrossGroupsByDate &&
    Array.isArray(coachHomeGroups) &&
    coachHomeGroups.length > 0 &&
    selectedDate;

  const sessionsList = (
    <SessionsList
      setSelectedSession={setSelectedSession}
      selectedSession={selectedSession}
      selectedgrp={selectedgrp}
      selectedDate={selectedDate}
      coachHomeGroups={coachHomeGroups}
      aggregateCoachSessionsAcrossGroupsByDate={
        aggregateCoachSessionsAcrossGroupsByDate
      }
      coachGroupsListReady={coachGroupsListReady}
      addSessionGroupOptions={addSessionGroupOptions}
      onSessionAddGroupChosen={onSessionAddGroupChosen}
      playerHomeMode={playerHomeMode}
    />
  );

  return (
    <>
      {selectedDate && (
        <div className="other-training-options training">
          <p className="sessionselectedgrp">
            {showAllGroupsHeader ? (
              <span>
                {moment(selectedDate).format("DD/MM/YYYY")}
                {" — "}
                {t("homeSessionsByDate") || "All your sessions by date"}
              </span>
            ) : (
              <>
                {selectedgrp?.groupDetails?.gr_name || selectedgrp?.gr_name || ""}
                <span style={{ marginLeft: "10px", color: "#666", fontSize: "0.9em" }}>
                  - {moment(selectedDate).format("DD/MM/YYYY")}
                </span>
              </>
            )}
          </p>
        </div>
      )}

      {/* Keep mounted without a calendar date so add-session dialogs / listeners work */}
      <div
        className="home-date-sessions"
        style={{ display: selectedDate ? "block" : "none" }}
        aria-hidden={!selectedDate}
      >
        {sessionsList}
      </div>
    </>
  );
};
